'use client';

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useGetMyChainedStrategiesQuery } from 'src/api/redux';
import { STRATEGY_COLORS, StrategyColor } from 'src/components/Strategy/constants';

const MAX_VISIBLE_BADGES = 1;

interface StrategyChainsChainMeta {
  label: string;
  target_strategy_type?: string;
}

interface StrategyChainsProps {
  chainIds: number[];
  chainsMetadata: Record<string | number, StrategyChainsChainMeta>;
}

const StrategyChainsCell = ({
  chainIds,
  chainsMetadata,
}: StrategyChainsProps) => {
  const { currentData: chainedStrategies } = useGetMyChainedStrategiesQuery();
  const [isOpen, setIsOpen] = useState(false);
  const [isHoveringDropdown, setIsHoveringDropdown] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, openLeft: false });
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const chainColorMap = useMemo(() => {
    const allIds = (chainedStrategies?.results || [])
      .map((s: any) => s.id)
      .sort((a: number, b: number) => a - b);
    const map = new Map<number, StrategyColor>();
    allIds.forEach((id: number, index: number) => {
      map.set(id, STRATEGY_COLORS[index % STRATEGY_COLORS.length]);
    });
    return map;
  }, [chainedStrategies?.results]);

  const show = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const openLeft = rect.left + 200 > window.innerWidth;
      setPosition({
        top: rect.bottom + 4,
        left: openLeft ? rect.right : rect.left,
        openLeft,
      });
    }
    setIsOpen(true);
  };

  const scheduleHide = () => {
    hideTimeoutRef.current = setTimeout(() => {
      if (!isHoveringDropdown) {
        setIsOpen(false);
      }
    }, 100);
  };

  const handleDropdownEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsHoveringDropdown(true);
  };

  const handleDropdownLeave = () => {
    setIsHoveringDropdown(false);
    setIsOpen(false);
  };

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // Close on scroll
  useEffect(() => {
    if (!isOpen) return;
    const close = () => setIsOpen(false);
    window.addEventListener('scroll', close, { capture: true, passive: true });
    return () =>
      window.removeEventListener('scroll', close, {
        capture: true,
      } as EventListenerOptions);
  }, [isOpen]);

  if (chainIds.length === 0) {
    return <span className="text-tertiary" data-testid="strategy-chains-empty">—</span>;
  }

  if (chainIds.length <= MAX_VISIBLE_BADGES) {
    return (
      <div className="flex flex-col gap-1" data-testid="strategy-chains-badges" role="list" aria-label="Strategy memberships">
        {chainIds.map((id) => {
          const color = chainColorMap.get(id) || STRATEGY_COLORS[0];
          const label = chainsMetadata[id]?.label || `Strategy ${id}`;
          return (
            <span
              key={id}
              role="listitem"
              data-testid={`strategy-chain-badge-${id}`}
              aria-label={label}
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium w-fit max-w-full ${color.pill}`}
            >
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${color.dot}`}
                aria-hidden="true"
              />
              <span className="line-clamp-2">{label}</span>
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div
      ref={triggerRef}
      data-testid="strategy-chains-trigger"
      aria-label={`${chainIds.length} strategies`}
      onMouseEnter={show}
      onMouseLeave={scheduleHide}
    >
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-primary w-fit cursor-default">
        <span className="flex -space-x-1" aria-hidden="true">
          {chainIds.slice(0, 3).map((id) => {
            const color = chainColorMap.get(id) || STRATEGY_COLORS[0];
            return (
              <span
                key={id}
                className={`w-2 h-2 rounded-full ring-1 ring-white flex-shrink-0 ${color.dot}`}
              />
            );
          })}
        </span>
        {chainIds.length} Strategies
      </span>
      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            data-testid="strategy-chains-dropdown"
            role="tooltip"
            aria-label="Strategy memberships"
            className="fixed z-[9999] flex flex-col gap-2 bg-white rounded-xl shadow-lg border border-neutral-200 p-3 min-w-max pointer-events-auto"
            style={{
              top: position.top,
              ...(position.openLeft
                ? { right: window.innerWidth - position.left }
                : { left: position.left }),
            }}
            onMouseEnter={handleDropdownEnter}
            onMouseLeave={handleDropdownLeave}
          >
            {chainIds.map((id) => {
              const color = chainColorMap.get(id) || STRATEGY_COLORS[0];
              const label = chainsMetadata[id]?.label || `Strategy ${id}`;
              return (
                <span
                  key={id}
                  data-testid={`strategy-chain-dropdown-item-${id}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${color.pill}`}
                >
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${color.dot}`}
                    aria-hidden="true"
                  />
                  {label}
                </span>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
};

export default StrategyChainsCell;
